import { useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useEnhancedToast } from './useEnhancedToast';

export type ChatMessage = {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
};

export const useAIChat = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const { showError } = useEnhancedToast();

  const sendMessage = useCallback(async (
    content: string,
    context?: { currentPage?: string; specificData?: any }
  ) => {
    if (!content.trim() || isStreaming) return;

    const userMessage: ChatMessage = {
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setIsStreaming(true);

    // Create abort controller for this request
    abortControllerRef.current = new AbortController();

    let assistantMessage = '';
    const assistantMessageIndex = messages.length + 1;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-chat`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            messages: [...messages, userMessage].map(m => ({
              role: m.role,
              content: m.content,
            })),
            context,
          }),
          signal: abortControllerRef.current.signal,
        }
      );

      if (!response.ok) {
        if (response.status === 429) {
          throw new Error('Rate limit exceeded. Please wait a moment before trying again.');
        }
        if (response.status === 402) {
          throw new Error('AI service unavailable. Please contact support.');
        }
        throw new Error('Failed to get response from AI');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      
      if (!reader) {
        throw new Error('No response stream available');
      }

      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.trim() || line.startsWith(':')) continue;
          if (!line.startsWith('data: ')) continue;

          const data = line.slice(6).trim();
          if (data === '[DONE]') continue;

          try {
            const parsed = JSON.parse(data);
            const delta = parsed.choices?.[0]?.delta?.content;
            
            if (delta) {
              assistantMessage += delta;
              
              setMessages(prev => {
                const newMessages = [...prev];
                const lastMessage = newMessages[assistantMessageIndex];
                
                if (lastMessage?.role === 'assistant') {
                  newMessages[assistantMessageIndex] = {
                    ...lastMessage,
                    content: assistantMessage,
                  };
                } else {
                  newMessages.push({
                    role: 'assistant',
                    content: assistantMessage,
                    timestamp: new Date(),
                  });
                }
                
                return newMessages;
              });
            }
          } catch (e) {
            console.warn('Failed to parse SSE data:', e);
          }
        }
      }

    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('Request cancelled');
        return;
      }
      
      console.error('Chat error:', error);
      showError(
        'Failed to send message',
        error instanceof Error ? error.message : 'Unknown error occurred'
      );
      
      // Remove the user message if we failed
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsStreaming(false);
      abortControllerRef.current = null;
    }
  }, [messages, isStreaming, showError]);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  const cancelStream = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setIsStreaming(false);
    }
  }, []);

  const toggleOpen = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const closeChat = useCallback(() => {
    setIsOpen(false);
  }, []);

  return {
    messages,
    isStreaming,
    isOpen,
    sendMessage,
    clearMessages,
    cancelStream,
    toggleOpen,
    closeChat,
  };
};
